/**
 * ブログ記事・ウェブページの編集画面の各フィールドをカスタマイズします。
 *
 * Updated: 2023/10/01
 *
 * @param {Object} options
 * @param {String} options.basename - 各フォーム要素のベースネーム。
 * @param {String} options.label - 変更後のラベル名。
 * @param {String} options.addClass - 追加するクラス名。
 * @param {String} options.hint - ヒントに表示させたいメッセージ。
 * @param {String} options.showField - 強制表示('show')、強制表示('hide')(注:basenameがbodyまたはmoreの場合はタブの表示制御）。
 * @param {String} options.showParent - 強制表示('show')、強制非表示('hide')(注:showParentは、basenameがbodyまたはmoreのみ）。
 * @param {Boolean} options.custom - カスタムフィールドの場合true。
 * @param {Boolean} options.widget - ウィジェットの場合true。
 * @param {Boolean} options.edit - 非編集モードにする場合true。
 * @returns {HTMLElement} - 対象のHTML要素。
 */
import { showHint } from './showHint.js';

export function customize(options) {
    const defaults = {
        basename: '',
        label: '',
        addClass: '',
        hint: '',
        showField: '',
        showParent: '',
        custom: false,
        widget: false,
        edit: false
    };

    // Optionsをマージ
    const op = { ...defaults, ...options };

    const opB = op.custom ? `customfield_${op.basename}` : op.basename;
    let field, label, tab, hover;

    // basenameが空だったら何もしない
    if (opB === '') {
        alert('basenameが設定されていません');
        return;
    }

    // field, labelを取得
    switch (opB) {
        case 'body': {
            field = document.querySelector('#text-field');
            tab = field ? field.querySelector('#editor-header div.tab:nth-child(1)') : null;
            label = tab ? tab.querySelector('a') : null;
            hover = label;
            break;
        }
        case 'more': {
            field = document.querySelector('#text-field');
            tab = field ? field.querySelector('#editor-header div.tab:nth-child(2)') : null;
            label = tab ? tab.querySelector('a') : null;
            hover = label;
            break;
        }
        case 'status': {
            // ウィジェットを取得
            const statusWidget = document.querySelector('#entry-status-widget');
            if (!statusWidget) break;

            // 新しいラッパーDIVを作成
            const statusWrapperDiv = document.createElement('div');
            statusWrapperDiv.id = 'mtapp-entry-status-widget-wrapper';

            // ウィジェットを新しいDIVで囲む
            statusWidget.parentElement.insertBefore(statusWrapperDiv, statusWidget);
            statusWrapperDiv.appendChild(statusWidget);

            // field変数に新しいDIVを代入
            field = statusWrapperDiv;

            label = field.querySelector('.mt-widget__title');
            hover = field;
            break;
        }
        case 'publishing': {
            // ウィジェットを取得
            const publishingWidget = document.querySelector('#entry-publishing-widget');
            if (!publishingWidget) break;

            // 新しいラッパーDIVを作成
            const publishingWrapperDiv = document.createElement('div');
            publishingWrapperDiv.id = 'mtapp-entry-publishing-widget-wrapper';

            // ウィジェットを新しいDIVで囲む
            publishingWidget.parentElement.insertBefore(publishingWrapperDiv, publishingWidget);
            publishingWrapperDiv.appendChild(publishingWidget);

            // field変数に新しいDIVを代入
            field = publishingWrapperDiv;

            label = field.querySelector('.mt-widget__title');
            hover = field;
            break;
        }
        case 'category': {
            field = document.querySelector('#category-field-wrapper');
            label = field ? field.querySelector('.mt-widget__title') : null;
            hover = field;
            break;
        }
        case 'feedback': {
            field = document.querySelector('#feedback-field');
            label = field ? field.querySelector('.mt-widget__title') : null;
            hover = field;
            break;
        }
        case 'assets': {
            field = document.querySelector('#assets-field');
            label = field ? field.querySelector('.mt-widget__title') : null;
            hover = field;
            break;
        }
        default: {
            if (op.widget) {
                field = document.querySelector(`#entry-${opB}-widget`);
                label = field ? field.querySelector('.mt-widget__title') : null;
            } else {
                field = document.querySelector(`#${opB}-field`);
                label = field ? field.querySelector('label') : null;
            }
            hover = field;
            break;
        }
    }

    // 対象フィールドが存在しないページ（ダイアログ内の管理画面ページ等）では何もしない。
    // user.js は「画像の挿入」等のダイアログ iframe にも読み込まれるため、ここで例外を
    // 投げると user.js の以降の処理がすべて中断されてしまう。
    if (!field) {
        console.warn(`[mtapp.customize] 対象フィールドが見つかりません: basename="${op.basename}"（このページには存在しない可能性があります）`);
        return null;
    }

    // フィールドにクラス名を追加
    if (op.addClass) {
        field.classList.add(...op.addClass.trim().split(' '));
    }

    // ラベルの変更
    if (op.label && label) {
        if (opB === 'category') {
            let textNodeFound = false;
            for (let node of label.childNodes) {
                // テキストノードの場合
                if (node.nodeType === 3) {
                    // テキストノードの内容を変更
                    node.textContent = op.label;
                    textNodeFound = true;
                    break;
                }
            }
            // テキストノードが見つからなかった場合、新しいテキストノードを追加する
            if (!textNodeFound) {
                const newText = document.createTextNode(op.label);
                label.insertBefore(newText, label.firstChild);
            }
        } else {
            label.textContent = op.label;
        }

        if (opB === 'title') {
            const placeholderElement = field.querySelector('#title');
            if (placeholderElement) {
                placeholderElement.setAttribute('placeholder', op.label);
            }
        }
    }

    // フィールドの表示・非表示
    if (op.showField === 'show') {
        if (opB === 'body' || opB === 'more') {
            if (tab) tab.classList.remove('d-none');
        } else {
            field.classList.remove('d-none');
            field.classList.add('d-block');
        }
    } else if (op.showField === 'hide') {
        if (opB !== 'body' && opB !== 'more') {
            field.classList.add('d-none');
            field.classList.remove('d-block');
        } else {
            if (tab) tab.classList.add('d-none');
        }
    }

    // テキストフィールドの表示・非表示
    if ((opB === 'body' || opB === 'more') && op.showParent === 'hide') {
        Object.assign(field.style, {
            position: 'absolute',
            top: '-9999px',
            left: '-9999px',
            width: '1px',
            height: '1px'
        });
    } else if ((opB === 'body' || opB === 'more') && op.showParent === 'show') {
        field.removeAttribute('style');
    }

    // ヒントの表示
    if (op.hint) {
        showHint(hover, { html: op.hint });
    }

    // 非編集モード
    if (op.edit) {
        let contentElement;
        if (op.widget === true) {
            contentElement = field.querySelector('.mt-widget');
        } else {
            contentElement = field.querySelector('.mt-draggable__content');
        }
        if (contentElement) {
            // 透明なDIVを作成
            const overlayDiv = document.createElement('div');
            Object.assign(overlayDiv.style, {
                position: 'absolute',
                top: '0',
                left: '0',
                width: '100%',
                height: '100%',
                zIndex: '10', // zIndexを上げてコンテンツの上に配置
                backgroundColor: 'rgba(255, 255, 255, 0.5)' // 半透明にする
            });

            // コンテンツ要素の親要素のpositionをrelativeに設定
            contentElement.parentElement.style.position = 'relative';

            // コンテンツ要素の親に透明なDIVを追加
            contentElement.parentElement.appendChild(overlayDiv);

            // 「編集」ボタンを作成
            const editButton = document.createElement('button');
            editButton.type = 'button';
            editButton.classList.add('btn', 'btn-default');
            editButton.innerHTML = `<svg role="img" class="mt-icon--edit mt-icon--sm">${mtappIcons.edit}</svg> 編集`; // SVGアイコンを追加

            // 指定されたスタイルを適用
            Object.assign(editButton.style, {
                position: 'absolute',
                top: '5px',
                right: '5px',
                zIndex: '20' // zIndexをoverlayDivよりも上げてクリック可能にする
            });

            editButton.addEventListener('click', () => {
                overlayDiv.remove(); // 透明なDIVを削除
                editButton.remove(); // ボタン自体も削除
            });

            // overlayDivに「編集」ボタンを追加
            overlayDiv.appendChild(editButton);
        }
    }

    return field;
}
